import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import "./SingaporeIndiaExpansion.css";

const API = process.env.REACT_APP_BACKEND_URL;

/* ───────── SVG Hero Visual ───────── */
const HeroVisual = () => (
  <svg
    viewBox="0 0 480 260"
    className="sie-hero-svg"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Singapore to India expansion visual"
  >
    {/* Singapore abstract skyline */}
    <rect x="30" y="100" width="28" height="70" fill="#059669" opacity="0.75" />
    <rect x="64" y="75" width="28" height="95" fill="#059669" opacity="0.60" />
    <rect x="98" y="110" width="28" height="60" fill="#059669" opacity="0.45" />
    <rect x="80" y="45" width="8" height="125" fill="#059669" opacity="0.85" />
    <line x1="20" y1="170" x2="140" y2="170" stroke="#059669" strokeWidth="2" opacity="0.5" />

    {/* Connector line + dots */}
    <line x1="155" y1="130" x2="310" y2="130" stroke="#D1D5DB" strokeWidth="1.5" strokeDasharray="4 4" />
    {[0, 1, 2, 3, 4].map((i) => (
      <circle
        key={i}
        className="sie-dot"
        cx={175 + i * 32}
        cy={130}
        r="4.5"
        fill="#059669"
        style={{ animationDelay: `${i * 0.3}s` }}
      />
    ))}

    {/* India abstract — geometric shape with 3 city nodes */}
    <polygon points="380,60 340,170 420,170" fill="none" stroke="#2563EB" strokeWidth="2" opacity="0.45" />
    <circle cx="380" cy="100" r="6" fill="#2563EB" opacity="0.7" />
    <circle cx="358" cy="148" r="6" fill="#2563EB" opacity="0.7" />
    <circle cx="402" cy="148" r="6" fill="#2563EB" opacity="0.7" />
    {/* Small connector lines between nodes */}
    <line x1="380" y1="106" x2="358" y2="142" stroke="#2563EB" strokeWidth="1" opacity="0.3" />
    <line x1="380" y1="106" x2="402" y2="142" stroke="#2563EB" strokeWidth="1" opacity="0.3" />
    <line x1="358" y1="148" x2="402" y2="148" stroke="#2563EB" strokeWidth="1" opacity="0.3" />

    {/* Below connector text */}
    <text x="240" y="210" textAnchor="middle" fill="#6B7280" fontSize="12" fontFamily="Inter, sans-serif" fontWeight="500">
      Strategy → Entry → Setup → Revenue
    </text>
  </svg>
);

/* ───────── Scroll helper ───────── */
const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

/* ───────── Main Page ───────── */
export default function SingaporeIndiaExpansion() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    objective: "",
    timeline: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.company || !form.objective || !form.timeline) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/india-expansion/book`, form);
      if (res.data.success) {
        toast.success("Submitted! We'll respond within 24 hours.");
        setForm({ name: "", email: "", phone: "", company: "", objective: "", timeline: "" });
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sie-page" data-testid="singapore-india-expansion-page">
      {/* ─── S1: HERO ─── */}
      <section className="sie-section sie-hero" data-testid="hero-section">
        <div className="sie-hero-inner">
          <div className="sie-hero-text">
            <span className="sie-eyebrow" data-testid="hero-eyebrow">Singapore → India Expansion</span>
            <h1 className="sie-h1" data-testid="hero-headline">
              Enter India in 90 Days — With Execution, Not Just Strategy
            </h1>
            <p className="sie-supporting">
              From first partner discussion to on-ground execution in India.
            </p>
            <div className="sie-hero-btns">
              <button
                className="btn-primary"
                data-testid="hero-book-call-btn"
                onClick={() => scrollTo("sie-form")}
              >
                Book India Entry Call (15-min)
              </button>
              <button
                className="btn-secondary"
                data-testid="hero-mra-fit-btn"
                onClick={() => scrollTo("sie-mra")}
              >
                Check MRA Fit (2-min)
              </button>
            </div>
            <p className="sie-trust">
              India–Singapore corridor specialists · Operator-led execution · MRA-aligned delivery
            </p>
          </div>
          <div className="sie-hero-visual">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* ─── S2: WHY INDIA ─── */}
      <section className="sie-section sie-alt" data-testid="why-india-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">Why India is a Strategic Expansion Market</h2>
          </div>
          <div className="sie-grid-4">
            {[
              { title: "1.4B+ Market", desc: "Large and growing demand" },
              { title: "Digital Ecosystem", desc: "Strong infrastructure and adoption" },
              { title: "Talent Access", desc: "Skilled, cost-efficient workforce" },
              { title: "Cost Efficiency", desc: "Scale with lower operating cost" },
            ].map((c) => (
              <div className="sie-card" key={c.title} data-testid={`why-india-card-${c.title.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── S3: WHO THIS IS FOR ─── */}
      <section className="sie-section" data-testid="who-for-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">Who This Is For</h2>
          </div>
          <div className="sie-grid-3">
            {[
              { title: "Singapore SMEs", desc: "Entering India with structured support" },
              { title: "Tech & Services", desc: "Building India teams and delivery hubs" },
              { title: "Manufacturing & Sourcing", desc: "Expanding supply chain and production" },
            ].map((c) => (
              <div className="sie-card" key={c.title}>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
          <p className="sie-below">
            Best suited for companies with active India plans in the next 3–6 months.
          </p>
        </div>
      </section>

      {/* ─── S4: WHY EXPANSION FAILS ─── */}
      <section className="sie-section sie-alt" data-testid="why-fails-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">Why Most India Expansion Fails</h2>
          </div>
          <div className="sie-table-wrap">
            <table className="sie-table" data-testid="comparison-table">
              <thead>
                <tr>
                  <th>Typical Approach</th>
                  <th>Bizenntrum Approach</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Enters without local clarity", "Defined entry blueprint"],
                  ["Random partner outreach", "Curated partner mapping"],
                  ["Treats grant as solution", "Execution-first approach"],
                  ["No India presence", "On-ground execution support"],
                  ["Weak financial planning", "Structured market-entry economics"],
                ].map(([l, r], i) => (
                  <tr key={i}>
                    <td className="sie-td-bad">{l}</td>
                    <td className="sie-td-good">{r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="sie-below sie-bold">
            Execution — not intent — determines success.
          </p>
        </div>
      </section>

      {/* ─── S5: EXPANSION ENGINE ─── */}
      <section className="sie-section" data-testid="expansion-engine-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">Your India Expansion Engine</h2>
          </div>
          <div className="sie-flow">
            {[
              { label: "Strategy", sub: "Entry model" },
              { label: "Entry", sub: "Partner access" },
              { label: "Setup", sub: "Legal + tax" },
              { label: "Scale", sub: "Hiring + growth" },
            ].map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <div className="sie-flow-box" data-testid={`flow-box-${s.label.toLowerCase()}`}>
                  <span className="sie-flow-label">{s.label}</span>
                  <span className="sie-flow-sub">{s.sub}</span>
                </div>
                {i < arr.length - 1 && <span className="sie-flow-arrow">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ─── S6: WHAT WE ENABLE ─── */}
      <section className="sie-section sie-alt" data-testid="achieve-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">What We Enable</h2>
          </div>
          <div className="sie-grid-4">
            {[
              { title: "Qualified Partner Access", desc: "5–10 relevant India partner connects" },
              { title: "India Setup", desc: "Entity and structuring support" },
              { title: "Local Team Build", desc: "Hiring support for India operations" },
              { title: "Business Connects", desc: "Direct customer conversations" },
            ].map((c) => (
              <div className="sie-card" key={c.title}>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
          <p className="sie-below">Measured outcomes — not advisory reports.</p>
        </div>
      </section>

      {/* ─── S7: TIMELINE ─── */}
      <section className="sie-section" data-testid="timeline-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">Typical India Expansion Journey (90 Days)</h2>
          </div>
          <div className="sie-timeline-row">
            {[
              { phase: "Days 1–30", desc: "Strategy & planning", detail: "Market assessment, entry model, partner shortlist" },
              { phase: "Days 31–60", desc: "Setup & structuring", detail: "Entity setup, compliance, hiring" },
              { phase: "Days 61–90", desc: "Go-live & execution", detail: "Launch, customer outreach, execution" },
            ].map((t) => (
              <div className="sie-timeline-box" key={t.phase} data-testid={`timeline-${t.phase.replace(/\s/g, "-").toLowerCase()}`}>
                <h3>{t.phase}</h3>
                <p className="sie-timeline-main">{t.desc}</p>
                <p className="sie-timeline-detail">{t.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── S8: MRA ALIGNMENT ─── */}
      <section className="sie-section sie-alt" id="sie-mra" data-testid="mra-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">Aligned with MRA-Supported Expansion</h2>
          </div>
          <div className="sie-mra-content">
            <p>We support execution aligned with MRA-supported activities such as:</p>
            <ul>
              <li>Partner identification and business development</li>
              <li>Market entry setup and structuring</li>
              <li>On-ground execution support</li>
            </ul>
            <p className="sie-mra-final">
              Our delivery aligns with the documentation and outcome expectations required for MRA claims.
            </p>
          </div>
        </div>
      </section>

      {/* ─── S9: WHERE WE FOCUS ─── */}
      <section className="sie-section" data-testid="focus-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title" style={{ marginBottom: "12px" }}>Where We Focus</h2>
          </div>
          <div className="sie-focus-cols">
            <div className="sie-focus-col sie-focus-not">
              <h3>Not Our Focus</h3>
              <ul>
                <li>Initial market research</li>
                <li>Basic feasibility studies</li>
                <li>Grant documentation preparation</li>
              </ul>
            </div>
            <div className="sie-focus-col sie-focus-yes">
              <h3>Where We Deliver</h3>
              <ul>
                <li>Market entry execution</li>
                <li>Partner identification and onboarding</li>
                <li>India setup and structuring</li>
                <li>On-ground operations support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── S10: FORM ─── */}
      <section className="sie-section sie-alt" id="sie-form" data-testid="form-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">Get Your India Entry Plan</h2>
          </div>
          <form className="sie-form" onSubmit={handleSubmit} data-testid="india-entry-form">
            <div className="sie-form-grid">
              <div className="form-group">
                <label className="form-label">Name</label>
                <input name="name" placeholder="Your full name" value={form.name} onChange={handleChange} className="form-input" data-testid="form-name" />
              </div>
              <div className="form-group">
                <label className="form-label">Work Email</label>
                <input name="email" type="email" placeholder="you@company.com" value={form.email} onChange={handleChange} className="form-input" data-testid="form-email" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input name="phone" placeholder="+65 9876 5432" value={form.phone} onChange={handleChange} className="form-input" data-testid="form-phone" />
              </div>
              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input name="company" placeholder="Your company" value={form.company} onChange={handleChange} className="form-input" data-testid="form-company" />
              </div>
              <div className="form-group">
                <label className="form-label">Expansion Objective</label>
                <input name="objective" placeholder="E.g., Set up India entity, find partners" value={form.objective} onChange={handleChange} className="form-input" data-testid="form-objective" />
              </div>
              <div className="form-group">
                <label className="form-label">Expected Timeline</label>
                <input name="timeline" placeholder="E.g., 3 months, Q2 2026" value={form.timeline} onChange={handleChange} className="form-input" data-testid="form-timeline" />
              </div>
            </div>
            <button type="submit" className="btn-primary sie-submit-btn" disabled={submitting} data-testid="form-submit-btn">
              {submitting ? "Submitting..." : "Get My India Entry Plan"}
            </button>
          </form>
          <p className="sie-below">We respond within 24 hours.</p>
        </div>
      </section>

      {/* ─── S11: FILTER ─── */}
      <section className="sie-section" data-testid="filter-section">
        <div className="sie-inner">
          <div className="section-header">
            <h2 className="section-title">This Is Not for Everyone</h2>
          </div>
          <div className="sie-filter-content">
            <p>Not suitable for:</p>
            <ul>
              <li>Early-stage idea exploration</li>
              <li>Companies without defined expansion intent</li>
              <li>Grant-only exploration</li>
            </ul>
          </div>
          <p className="sie-below">
            Best suited for companies entering India within 3–6 months.
          </p>
        </div>
      </section>

      {/* ─── S12: FINAL CTA ─── */}
      <section className="sie-section sie-final-cta" data-testid="final-cta-section">
        <div className="sie-inner">
          <h2 className="section-title">Ready to Enter India?</h2>
          <p className="sie-cta-sub">Start with a focused 15-minute discussion.</p>
          <div className="sie-hero-btns sie-cta-btns">
            <button className="btn-primary" data-testid="final-book-call-btn" onClick={() => scrollTo("sie-form")}>
              Book India Entry Call
            </button>
            <button className="btn-secondary" data-testid="final-mra-fit-btn" onClick={() => scrollTo("sie-mra")}>
              Check MRA Fit
            </button>
          </div>
          <p className="sie-capacity">Limited onboarding capacity each month.</p>
        </div>
      </section>

      {/* ─── S13: FOOTER DISCLAIMER ─── */}
      <div className="sie-disclaimer" data-testid="footer-disclaimer">
        <p>
          Bizenntrum is an independent advisory and execution partner. We do not represent any government authority. Grant approvals are subject to Enterprise Singapore guidelines.
        </p>
      </div>
    </div>
  );
}
